import { useMutation } from '@apollo/client';
import type {
  NotificationFrequency,
  NotificationProduct,
  NotificationSubscription,
} from '@seek/ca-graphql-schema/types';
import { useMelwaysInfo } from '@seek/melways-react';
import { Column, Columns, Stack, Text, Toggle } from 'braid-design-system';
import type { FunctionComponent } from 'react';

import { useTypedSelector } from 'src/reducers';
import type {
  UpdateNotificationSubscriptionMutation,
  UpdateNotificationSubscriptionMutationVariables,
} from 'src/types/graphql';

import { updatePreferenceQL } from '../../modules/graphql/notifications/preference.graphql';

import ProductRowToggleOptions from './product-sub-row-options';
import { trackToggle } from './tracking/tealium';

interface ProductRowToggleProps {
  product: NotificationProduct;
  subscriptions: NotificationSubscription[];
}

const ProductRowToggle: FunctionComponent<ProductRowToggleProps> = ({
  product,
  subscriptions,
}) => {
  const { language } = useMelwaysInfo();
  const locale = useTypedSelector(
    (state) => state.page.pageAnalytics.siteCountry,
  ).toUpperCase();

  const [updateSubscriptions] = useMutation<
    UpdateNotificationSubscriptionMutation,
    UpdateNotificationSubscriptionMutationVariables
  >(updatePreferenceQL);

  const updateCallback =
    (channelCode: string, id: string, frequency?: NotificationFrequency) =>
    (subscribe: boolean) => {
      trackToggle({
        product: product.code,
        channel: channelCode,
        subscribed: subscribe,
        frequency: frequency?.value,
      });
      updateSubscriptions({
        variables: {
          input: {
            productCode: product.code,
            channelCode,
            subscribe,
            frequency: frequency?.value ?? null,
            locale: `${language}-${locale}`,
          },
        },
        optimisticResponse: buildOptimisticResponse({
          id,
          subscribed: subscribe,
          frequency,
        }),
      });
    };

  return (
    <div data-testid={`product-${product.code}`}>
      <Columns space={['gutter', 'xlarge']} collapseBelow="tablet">
        <Column>
          <Stack space="gutter">
            <Text weight="strong">{product.label}</Text>
            <Text tone="secondary">{product.description}</Text>
          </Stack>
        </Column>
        <Column width="1/4">
          <Stack space="gutter">
            {subscriptions.map(({ channel, subscribed, id, frequency }) => (
              <Toggle
                key={id}
                align="justify"
                label={channel.label}
                on={subscribed}
                id={`${product.code}-${channel.code}`}
                onChange={updateCallback(
                  channel.code,
                  id,
                  frequency ?? undefined,
                )}
              />
            ))}
          </Stack>
        </Column>
      </Columns>

      {subscriptions.map(({ frequency, subscribed, id, channel }) => {
        if (subscribed && frequency) {
          return (
            <ProductRowToggleOptions
              key={id}
              id={id}
              frequency={frequency}
              product={product}
              channelCode={channel.code}
            />
          );
        }
      })}
    </div>
  );
};

function buildOptimisticResponse({
  id,
  subscribed,
  frequency,
}: {
  id: string;
  subscribed: boolean;
  frequency?: NotificationFrequency;
}): UpdateNotificationSubscriptionMutation {
  return {
    updateNotificationSubscription: {
      __typename: 'NotificationPreference',
      subscriptions: [
        {
          id,
          subscribed,
          frequency: frequency?.value
            ? {
                value: frequency.value,
                options: frequency.options,
                __typename: 'NotificationFrequency',
              }
            : null,
          __typename: 'NotificationSubscription',
        },
      ],
    },
  };
}

ProductRowToggle.defaultProps = {
  subscriptions: [],
};

export default ProductRowToggle;
